import * as React from "react";
import {
  SketchPicker,
  ColorResult
} from "react-color";
import styled from "styled-components";

import Grid from "src/library/components/Hedron";
import { Button } from "src/library/components/Button";
import { Icon } from "src/library/components/Icon";
import { Text } from "src/library/components/Text";
import { Colors } from "src/library/utils/theme";
import { useEventListener } from "src/library/hooks/useEventListener";
import { Icons } from "src/library/components/Icon/types";

interface ColorPickerButtonProps {
  id: string;
  label: string;
  color: keyof Colors;
  defaultColor: string;
  onSelect: (result: ColorResult) => void;
}

const ColorPickerButton: React.ReactFCWithChildren<ColorPickerButtonProps> = ({
  id, label, color, defaultColor, onSelect
}) => {
  const [ colorPickerVisible, setColorPickerVisible ] = React.useState(false);

  const toggleColorPicker = () => {
    setColorPickerVisible(isVisible => !isVisible);
  };

  const containerRef = React.useRef<HTMLDivElement>(null);

  // focus color picker container div on toggle, so blur event is triggered when user clicks away
  React.useEffect(() => {
    if (colorPickerVisible && containerRef.current) {
      containerRef.current.focus();
    }
  }, [ colorPickerVisible ]);

  // hide picker when user clicks elsewhere
  const clickHandler = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (containerRef.current && containerRef.current.contains && !containerRef.current.contains(event.target as Node)) {
        setColorPickerVisible(false);
      }
    },
    [ ]
  );

  useEventListener("mousedown", clickHandler, document);

  return (
    <ColorPickerButtonWrapper>
      <PickerButton
        id={id}
        color="white"
        $buttonType="plain"
        background={color}
        defaultColor={defaultColor}
        onClick={toggleColorPicker}
      >
        <Grid.Box
          direction="horizontal"
          style={{
            display: "flex",
            alignItems: "center",
            height: "35px"
          }}
        >
          <Icon
            icon={Icons.EDIT}
            size={20}
            $fillColor="white"
          />

          <Text
            color="white"
            style={{ marginLeft: 18 }}
          >
            {label}
          </Text>
        </Grid.Box>
      </PickerButton>

      <PickerWrapper
        ref={containerRef}
        tabIndex={0}
        style={{
          display: colorPickerVisible ? "block" : "none",
          zIndex: 1000
        }}
      >
        <StyledPicker
          themeColor={color}
          defaultColor={defaultColor}
          onChangeComplete={onSelect}
          width="138px"
        />
      </PickerWrapper>

    </ColorPickerButtonWrapper>
  );
};

export default ColorPickerButton;

const ColorPickerButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  &:not(:first-of-type) {
    margin-left: 24px;
  }
`;

const PickerButton = styled(Button)<{ defaultColor: string }>`
  background-color: ${({
    theme, background, defaultColor
  }) => (!!background && theme.colors[ background ] ? theme.colors[ background ] : defaultColor)};
`;

const PickerWrapper = styled.div`
  position: absolute;
  margin-top: 55px;
  &:focus {
    outline: none;
  }
`;

interface PickerProps {
  themeColor: keyof Colors;
  defaultColor: string;
}

const StyledPicker = styled(SketchPicker).attrs<PickerProps>(({
  theme, themeColor, defaultColor
}) => ({ color: theme.colors[ themeColor ] || defaultColor }))<PickerProps>`
  z-index: 5000;
`;
